import React, { useState, useEffect } from 'react';
import { Loader2, Copy, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { deletePaper } from '@/lib/api/paper-operations/delete-paper-service';
import DeletePaperDialog from '@/components/papers/DeletePaperDialog';

interface DuplicatePaper {
  id: string;
  title: string;
  abstract: string;
  file_url: string;
  external_url: string;
  uploaded_at: string;
  original_paper_id: string;
  original_paper_title: string;
  original_uploaded_at: string;
}

const DuplicatePapersSection: React.FC = () => {
  const [duplicates, setDuplicates] = useState<DuplicatePaper[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deletingPaperId, setDeletingPaperId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [paperToDelete, setPaperToDelete] = useState<DuplicatePaper | null>(null);

  const fetchDuplicatePapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_duplicate_papers');
      
      if (error) {
        throw error;
      }
      
      setDuplicates(data || []);
    } catch (error) {
      console.error('Error fetching duplicate papers:', error);
      toast.error('Failed to load duplicate papers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaper = async (paper: DuplicatePaper) => {
    setPaperToDelete(paper);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePaper = async () => {
    if (!paperToDelete) return;

    setDeletingPaperId(paperToDelete.id);
    try {
      const success = await deletePaper(paperToDelete.id);
      if (success) {
        await fetchDuplicatePapers(); // Refresh the list
        toast.success('Duplicate paper deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting paper:', error);
      toast.error('Failed to delete paper');
    } finally {
      setDeletingPaperId(null);
      setDeleteDialogOpen(false);
      setPaperToDelete(null);
    }
  };

  useEffect(() => {
    fetchDuplicatePapers();
  }, []);

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-orange-500" />
            Duplicate Papers (arXiv URLs)
            <Badge variant="outline" className="ml-2">
              {loading ? '...' : duplicates.length}
            </Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Papers with matching arXiv URLs. Delete the later-added duplicates to clean up the database.
          </p>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : duplicates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Copy className="h-12 w-12 mx-auto mb-2 text-green-500" />
              <p>No duplicate papers found!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Duplicate Paper</TableHead>
                  <TableHead>Original Paper</TableHead>
                  <TableHead>arXiv URL</TableHead>
                  <TableHead>Added Dates</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {duplicates.map((duplicate) => (
                  <TableRow key={duplicate.id}>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium truncate max-w-[200px]">
                            {duplicate.title}
                          </p>
                          <Badge variant="destructive" className="text-xs mt-1">
                            Duplicate
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div>
                        <p className="font-medium truncate max-w-[200px]">
                          {duplicate.original_paper_title}
                        </p>
                        <Badge variant="secondary" className="text-xs mt-1">
                          Original
                        </Badge>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <a 
                        href={duplicate.external_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        {duplicate.external_url.replace('https://', '').substring(0, 30)}...
                      </a>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium text-orange-600">Duplicate:</span>{' '}
                          {new Date(duplicate.uploaded_at).toLocaleDateString()}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium text-green-600">Original:</span>{' '}
                          {new Date(duplicate.original_uploaded_at).toLocaleDateString()}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={deletingPaperId === duplicate.id}
                        onClick={() => handleDeletePaper(duplicate)}
                      >
                        {deletingPaperId === duplicate.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete Duplicate
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DeletePaperDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDeletePaper}
        isDeleting={deletingPaperId !== null}
      />
    </>
  );
};

export default DuplicatePapersSection;